import React from 'react'
import { motion, AnimatePresence, type Variants } from 'framer-motion'
import { ArrowLeft, ArrowRight, Buildings, Info } from '@phosphor-icons/react'
import { toast } from 'sonner'
import { DotLoader } from '@/components/ui/DotLoader'
import { authApi, getErrorMessage } from '@/lib/api'
import { errorBorderClass } from '@/lib/styleKit'

/**
 * Two clearly-separated sign-in paths, rendered only when
 * VITE_WORKOS_AUTH_ENABLED === 'true':
 *
 *  1. PERSONAL ACCOUNT — "Continue with Google / Microsoft". WorkOS social
 *     OAuth; authenticates an individual's personal account.
 *  2. ORGANIZATION (SSO) — work-email Single Sign-On (any IdP behind the org's
 *     connection: Entra, Okta, Google Workspace, SAML/OIDC), plus a one-click
 *     "Continue with Microsoft (work account)" that routes to the configured
 *     work connection.
 */
const SSO_ENABLED = import.meta.env.VITE_WORKOS_AUTH_ENABLED === 'true'

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
}

const buttonClass =
  'w-full h-10 rounded-md bg-white border border-black/[0.08] text-[#0f1011] ' +
  'hover:bg-[#f5f5f5] hover:border-black/[0.14] dark:bg-white/[0.02] dark:border-white/[0.08] ' +
  'dark:text-[#e2e4e7] dark:hover:bg-white/[0.04] dark:hover:border-white/[0.12] ' +
  'inline-flex items-center justify-center gap-2.5 focus:outline-none focus:ring-2 ' +
  'focus:ring-[#5e6ad2]/30 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-[#0f1011] ' +
  'transition-colors duration-150 disabled:opacity-60 disabled:pointer-events-none'

const ssoEmailInputClass =
  'w-full h-10 rounded-md bg-white border border-black/[0.08] text-[#0f1011] ' +
  'dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#f7f8f8] ' +
  'placeholder:text-[#7a7d83] dark:placeholder:text-[#62666d] px-3 ' +
  'transition-colors duration-150 hover:border-black/[0.14] dark:hover:border-white/[0.14] ' +
  // §13: fields get a border-only focus tint, and the dark partner is mandatory
  // (§56.5) — without it the focus state is invisible on the dark surface.
  'focus:outline-none focus:border-[#7170ff]/55 dark:focus:border-[#7170ff]/75'

const captionStyle: React.CSSProperties = { fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.45 }

/** Same shape the SSO backend accepts; enough to catch a typo before a round-trip. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const GoogleIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
  </svg>
)

const MicrosoftIcon = () => (
  <svg className="h-4 w-4" viewBox="0 0 23 23" aria-hidden="true">
    <path fill="#F25022" d="M1 1h10v10H1z" />
    <path fill="#7FBA00" d="M12 1h10v10H12z" />
    <path fill="#00A4EF" d="M1 12h10v10H1z" />
    <path fill="#FFB900" d="M12 12h10v10H12z" />
  </svg>
)

/** Labeled section divider (e.g. "Sign in with a personal account"). */
const Divider: React.FC<{ label: string }> = ({ label }) => (
  <motion.div className="flex items-center gap-3 py-1" variants={itemVariants}>
    <div className="flex-1 border-t border-black/[0.08] dark:border-white/[0.05]" />
    <span
      className="text-[#7a7d83] dark:text-[#62666d] shrink-0"
      style={{
        fontFamily: "'Berkeley Mono', ui-monospace, 'SF Mono', Menlo, monospace",
        fontSize: '10px',
        fontWeight: 400,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </span>
    <div className="flex-1 border-t border-black/[0.08] dark:border-white/[0.05]" />
  </motion.div>
)

interface Props {
  /** Preserved post-login destination, round-tripped through WorkOS state. */
  redirect?: string
}

const SocialAuthButtons: React.FC<Props> = ({ redirect = '/dashboard' }) => {
  const [busy, setBusy] = React.useState<'google' | 'microsoft' | 'sso' | null>(null)
  const [ssoMode, setSsoMode] = React.useState(false)
  const [ssoEmail, setSsoEmail] = React.useState('')
  // Inline notice shown under the SSO form (e.g. "not set up for this domain").
  const [notice, setNotice] = React.useState<string | null>(null)
  // Field-level validation error for the work-email input.
  const [emailError, setEmailError] = React.useState<string | null>(null)

  if (!SSO_ENABLED) return null

  const startSocial = async (provider: 'google' | 'microsoft') => {
    setBusy(provider)
    try {
      const { data } = await authApi.ssoAuthorize({ provider, redirect })
      if (data.authorization_url) {
        window.location.assign(data.authorization_url)
        return
      }
      toast.error('Could not start sign-in. Please try again.')
      setBusy(null)
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, 'Could not start sign-in'))
      setBusy(null)
    }
  }

  const submitSso = async (e: React.FormEvent) => {
    e.preventDefault()
    const email = ssoEmail.trim()
    // Without this a malformed address silently did nothing on Continue — the
    // `disabled` guard on the button only covers the empty case.
    if (!email) {
      setEmailError('Enter your work email address.')
      return
    }
    if (!EMAIL_RE.test(email)) {
      setEmailError('Enter a valid work email address, e.g. you@yourcompany.com.')
      return
    }
    setEmailError(null)
    setBusy('sso')
    setNotice(null)
    try {
      const { data } = await authApi.ssoAuthorize({ mode: 'sso', email, redirect })
      if (data.authorization_url) {
        window.location.assign(data.authorization_url)
        return
      }
      setNotice(
        `Single Sign-On isn't enabled for ${data.domain || 'this domain'} yet. ` +
          `If you're an admin, sign in with your email and password above, then set up ` +
          `your organization under Settings → Security → Single Sign-On. Otherwise ask ` +
          `your admin to enable it.`,
      )
      setBusy(null)
    } catch (error: unknown) {
      setNotice(getErrorMessage(error, 'Could not start SSO. Please check the email and try again.'))
      setBusy(null)
    }
  }

  return (
    <div className="space-y-5">
      {/* ── 1) Personal account ─────────────────────────────────────── */}
      <div className="space-y-3">
        <Divider label="Personal account" />
        {/* The redirect to the IdP can take a moment; without a pending
            affordance the page just looks frozen (§55). */}
        <button
          type="button"
          disabled={busy !== null}
          aria-busy={busy === 'google'}
          onClick={() => startSocial('google')}
          className={buttonClass}
          style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}
        >
          {busy === 'google' ? <DotLoader size="sm" announce={false} /> : <GoogleIcon />}
          {busy === 'google' ? 'Connecting…' : 'Continue with Google'}
        </button>
        <button
          type="button"
          disabled={busy !== null}
          aria-busy={busy === 'microsoft'}
          onClick={() => startSocial('microsoft')}
          className={buttonClass}
          style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}
        >
          {busy === 'microsoft' ? <DotLoader size="sm" announce={false} /> : <MicrosoftIcon />}
          {busy === 'microsoft' ? 'Connecting…' : 'Continue with Microsoft'}
        </button>
      </div>

      {/* ── 2) Organization (SSO) ───────────────────────────────────── */}
      <div className="space-y-3">
        <Divider label="Single sign-on" />
        <AnimatePresence mode="wait" initial={false}>
          {!ssoMode ? (
            <motion.div
              key="org"
              className="space-y-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => { setSsoMode(true); setNotice(null); setEmailError(null) }}
                className={buttonClass}
                style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}
              >
                <Buildings className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="fill" />
                Sign in with SSO
              </button>
              <p className="text-center text-[#7a7d83] dark:text-[#62666d]" style={captionStyle}>
                Use your company identity provider — Microsoft Entra, Google Workspace, or any SAML/OIDC.
              </p>
            </motion.div>
          ) : (
            <motion.form
              key="sso"
              onSubmit={submitSso}
              className="space-y-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <div>
                <label
                  htmlFor="sso-email"
                  className="block mb-1.5 text-[#5f6166] dark:text-[#8a8f98]"
                  style={captionStyle}
                >
                  Work email
                </label>
                <input
                  id="sso-email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  placeholder="you@yourcompany.com"
                  value={ssoEmail}
                  aria-invalid={emailError ? true : undefined}
                  aria-describedby={emailError ? 'sso-email-error' : undefined}
                  onChange={(e) => { setSsoEmail(e.target.value); setNotice(null); setEmailError(null) }}
                  className={`${ssoEmailInputClass} ${emailError ? errorBorderClass : ''}`}
                  style={{ fontSize: '14px', letterSpacing: '-0.13px' }}
                />
                {emailError && (
                  <p
                    id="sso-email-error"
                    role="alert"
                    className="mt-1.5 text-red-600 dark:text-red-400"
                    style={captionStyle}
                  >
                    {emailError}
                  </p>
                )}
              </div>

              {notice && (
                <div
                  role="status"
                  aria-live="polite"
                  className="flex gap-2 rounded-md border border-amber-500/30 bg-amber-500/[0.06] dark:bg-amber-500/[0.08] px-3 py-2.5 text-amber-700 dark:text-amber-300"
                  style={captionStyle}
                >
                  <Info className="h-4 w-4 shrink-0 mt-0.5" weight="fill" />
                  <span>{notice}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={busy !== null || !ssoEmail.trim()}
                aria-busy={busy === 'sso'}
                className="w-full h-10 rounded-md bg-[#5e6ad2] text-white inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:pointer-events-none hover:bg-[#7170ff] active:bg-[#5058c0] transition-colors focus:outline-none focus:ring-2 focus:ring-[#5e6ad2]/40 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-[#0f1011]"
                style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}
              >
                {busy === 'sso' ? (
                  <>
                    <DotLoader size="sm" announce={false} />
                    <span>Connecting…</span>
                  </>
                ) : (
                  <>
                    Continue with SSO
                    <ArrowRight className="h-4 w-4" weight="bold" />
                  </>
                )}
              </button>

              <button
                type="button"
                disabled={busy !== null}
                onClick={() => { setSsoMode(false); setNotice(null); setSsoEmail(''); setEmailError(null) }}
                className="w-full inline-flex items-center justify-center gap-1.5 text-[#5f6166] hover:text-[#0f1011] dark:text-[#8a8f98] dark:hover:text-[#d0d6e0] transition-colors"
                style={captionStyle}
              >
                <ArrowLeft className="h-3.5 w-3.5" weight="bold" />
                Back
              </button>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default SocialAuthButtons
